import { defaultSchema } from 'rehype-sanitize';
import type { Schema } from 'hast-util-sanitize';
import { unified } from 'unified';
import rehypeParse from 'rehype-parse';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';

/**
 * URI schemes we are willing to navigate to or load from. Everything else --
 * notably `javascript:`, `vbscript:` and `file:` -- is rejected.
 *
 * `data:` is deliberately absent for links; a narrowly scoped allowance for
 * `data:image/*` is handled separately in {@link isSafeImageSrc}.
 */
export const SAFE_URL_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:', 'irc:', 'ircs:', 'xmpp:'];

/** Image `data:` payloads we accept. SVG is excluded: it can carry script. */
const SAFE_IMAGE_DATA_PREFIXES = [
  'data:image/png;',
  'data:image/jpeg;',
  'data:image/gif;',
  'data:image/webp;',
  'data:image/avif;',
];

const clone = <T>(value: T): T => structuredClone(value);

/**
 * The allowlist applied to every document before it becomes React elements.
 *
 * It starts from `hast-util-sanitize`'s GitHub-derived defaults (which already
 * exclude `script`, `iframe`, `object`, `embed`, `form`, `style` and every
 * `on*` event handler) and then re-adds only what this viewer needs.
 */
const buildSchema = (): Schema => {
  const schema = clone(defaultSchema) as Schema;

  schema.tagNames = [...(schema.tagNames ?? []), 'abbr', 'caption', 'colgroup', 'col', 'figure', 'figcaption', 'mark', 'small', 'time', 'u', 'wbr'];

  schema.attributes = {
    ...schema.attributes,
    // `target` is safe because MarkdownLink always pairs it with rel="noopener noreferrer".
    a: [...(schema.attributes?.a ?? []), ['target', '_blank', '_self'], 'rel', 'download'],
    // `math`/`math-inline`/`math-display` are emitted by remark-math and consumed by rehype-katex.
    span: [...(schema.attributes?.span ?? []), ['className', /^math(-inline|-display)?$/, /^katex/, 'search-hit']],
    div: [...(schema.attributes?.div ?? []), 'dataCallout', 'dataCalloutTitle', ['className', /^math(-inline|-display)?$/, /^callout/]],
    pre: [...(schema.attributes?.pre ?? []), ['className', /^language-./]],
    code: [...(schema.attributes?.code ?? []), 'dataMeta', ['className', /^language-./, /^math/]],
    img: [...(schema.attributes?.img ?? []), 'title', 'width', 'height', 'loading', 'decoding', 'srcSet', 'sizes'],
    td: [...(schema.attributes?.td ?? []), 'align', 'colSpan', 'rowSpan'],
    th: [...(schema.attributes?.th ?? []), 'align', 'colSpan', 'rowSpan', 'scope'],
    input: [...(schema.attributes?.input ?? []), ['className', 'task-list-item-checkbox'], 'checked'],
    li: [...(schema.attributes?.li ?? []), ['className', 'task-list-item']],
    time: ['dateTime'],
  };

  schema.protocols = {
    ...schema.protocols,
    href: ['http', 'https', 'mailto', 'tel', 'irc', 'ircs', 'xmpp'],
    src: ['http', 'https'],
  };

  // Discard these elements *and* their contents. Without this the sanitizer
  // keeps the text inside e.g. `<style>`, which is noise at best.
  schema.strip = [
    'script',
    'style',
    'noscript',
    'template',
    'iframe',
    'frame',
    'frameset',
    'object',
    'embed',
    'applet',
    'form',
    'svg',
    'base',
    'meta',
    'link',
    'title',
    'head',
  ];

  // Footnote ids are generated with an empty clobber prefix upstream, so the
  // sanitizer owns prefixing. `rehypeFixFragmentLinks` re-points hrefs afterwards.
  schema.clobberPrefix = 'user-content-';

  return schema;
};

export const sanitizeSchema: Schema = buildSchema();

/**
 * Schema for SVG produced by Mermaid. Mermaid output is machine-generated from
 * a diagram definition rather than copied from the document, so the risk profile
 * differs from author-supplied HTML.
 *
 * `style` is permitted because Mermaid scopes its stylesheet to the diagram's
 * generated id and it is required for legible output. Scripting vectors
 * (`script`, `foreignObject`, `a`, event handlers, external refs) stay blocked.
 */
export const mermaidSvgSchema: Schema = {
  tagNames: [
    'svg', 'g', 'defs', 'style', 'marker', 'path', 'rect', 'circle', 'ellipse', 'line',
    'polyline', 'polygon', 'text', 'tspan', 'title', 'desc', 'use', 'symbol', 'clipPath',
    'linearGradient', 'radialGradient', 'stop', 'pattern', 'mask', 'image',
  ],
  attributes: {
    '*': [
      'id', 'class', 'className', 'style', 'transform', 'width', 'height', 'x', 'y', 'x1', 'x2',
      'y1', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'd', 'points', 'fill', 'fillOpacity', 'fillRule',
      'stroke', 'strokeWidth', 'strokeDasharray', 'strokeLinecap', 'strokeLinejoin', 'strokeOpacity',
      'opacity', 'viewBox', 'preserveAspectRatio', 'markerEnd', 'markerStart', 'markerWidth',
      'markerHeight', 'refX', 'refY', 'orient', 'markerUnits', 'textAnchor', 'dominantBaseline',
      'alignmentBaseline', 'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'letterSpacing',
      'dx', 'dy', 'offset', 'stopColor', 'stopOpacity', 'gradientUnits', 'gradientTransform',
      'patternUnits', 'clipPath', 'clipRule', 'mask', 'xmlns', 'xmlnsXlink', 'spaceCollapse',
      'space', 'role', 'ariaLabel', 'ariaRoleDescription', 'visibility', 'display', 'overflow',
    ],
  },
  // Never let generated SVG reference remote or scriptable resources.
  protocols: { href: [], src: [] },
  strip: ['script', 'foreignObject', 'a', 'animate', 'animateTransform', 'set', 'handler'],
  clobber: [],
  clobberPrefix: '',
};

const defaultBase = (): string =>
  typeof window === 'undefined' ? 'https://markdown-viewer.invalid/' : window.location.href;

const parseUrl = (value: string, base?: string): URL | null => {
  try {
    return new URL(value, base ?? defaultBase());
  } catch {
    return null;
  }
};

/** Resolve a document-relative URL after it has passed the relevant URL policy. */
export const resolveDocumentUrl = (value: string, base?: string): string => {
  // A fragment refers to the heading in the currently displayed document.
  if (!base || value.startsWith('#')) return value;
  return parseUrl(value, base)?.href ?? value;
};

/** True when `href` is safe to navigate to. Relative and fragment URLs pass. */
export const isSafeUrl = (href: string | undefined | null, base?: string): boolean => {
  if (!href) return false;
  const trimmed = href.trim();
  if (trimmed === '') return false;
  // Control characters are deliberately targeted: they are used to smuggle
  // `java\nscript:` past naive protocol checks.
  // eslint-disable-next-line no-control-regex
  const normalized = trimmed.replace(/[\u0000-\u001F\u007F\s]/g, '').toLowerCase();
  if (/^(javascript|vbscript|data|blob|file):/.test(normalized)) return false;
  if (trimmed.startsWith('#')) return true;
  const url = parseUrl(trimmed, base);
  if (!url) return false;
  return SAFE_URL_PROTOCOLS.includes(url.protocol);
};

/** True when `src` is safe to load as an image. */
export const isSafeImageSrc = (src: string | undefined | null, base?: string): boolean => {
  if (!src) return false;
  const trimmed = src.trim();
  // eslint-disable-next-line no-control-regex
  const normalized = trimmed.replace(/[\u0000-\u001F\u007F\s]/g, '').toLowerCase();
  if (SAFE_IMAGE_DATA_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return true;
  if (/^(javascript|vbscript|data|blob|file):/.test(normalized)) return false;
  const url = parseUrl(trimmed, base);
  if (!url) return false;
  return url.protocol === 'http:' || url.protocol === 'https:';
};

/** True when the URL points somewhere other than the current origin. */
export const isExternalUrl = (href: string | undefined | null, base?: string): boolean => {
  if (!href || href.startsWith('#')) return false;
  const url = parseUrl(href, base);
  if (!url) return false;
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
  if (typeof window === 'undefined') return true;
  return url.origin !== window.location.origin;
};

const htmlSanitizer = unified()
  .use(rehypeParse, { fragment: true })
  .use(rehypeSanitize, sanitizeSchema)
  .use(rehypeStringify);

/**
 * Sanitize an HTML fragment string using the exact schema the render pipeline
 * uses. Provided for callers that must deal in strings; the viewer itself never
 * round-trips through HTML text.
 */
export const sanitize = (html: string): string => String(htmlSanitizer.processSync(html));
